import React, { useEffect, useState } from "react";
import { Smartphone, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "hpl_pwa_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 7;

/**
 * Floating "Install app" pill.
 * - On Android / desktop Chromium: listens for `beforeinstallprompt` and triggers the
 *   native install dialog on click.
 * - On iOS (Safari has no programmatic prompt): shows a short tutorial card
 *   explaining the "Share → Add to Home Screen" flow.
 * - Hidden if already running as an installed PWA, or if the user dismissed it
 *   within the last 7 days.
 */
export default function InstallPWA() {
    const [deferred, setDeferred] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [showIOSCard, setShowIOSCard] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const ua = window.navigator.userAgent || "";
        const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        setIsIOS(ios);

        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        setIsStandalone(standalone);

        const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (ts && Date.now() - ts < DISMISS_COOLDOWN_DAYS * 86400 * 1000) {
            setDismissed(true);
        }

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        const onInstalled = () => {
            setDeferred(null);
            setIsStandalone(true);
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setDismissed(true);
        setShowIOSCard(false);
    };

    const install = async () => {
        if (deferred) {
            deferred.prompt();
            const choice = await deferred.userChoice;
            setDeferred(null);
            if (choice.outcome !== "accepted") dismiss();
            return;
        }
        if (isIOS) setShowIOSCard(true);
    };

    if (isStandalone || dismissed) return null;
    if (!deferred && !isIOS) return null;

    return (
        <>
            <div className="fixed bottom-4 right-4 z-[60] sm:bottom-6 sm:right-6" data-testid="pwa-install-pill">
                <div className="bg-white border border-sky-700 shadow-lg flex items-stretch">
                    <button
                        type="button"
                        onClick={install}
                        className="px-4 py-3 flex items-center gap-2 text-sm font-medium text-sky-800 hover:bg-sky-50"
                        data-testid="pwa-install-button"
                    >
                        <Smartphone className="w-4 h-4" />
                        Install app
                    </button>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="px-2 border-l border-sky-200 text-stone-400 hover:bg-stone-50 hover:text-stone-700"
                        aria-label="Dismiss install prompt"
                        data-testid="pwa-install-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {showIOSCard && (
                <div
                    className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-4"
                    onClick={() => setShowIOSCard(false)}
                    data-testid="pwa-ios-card"
                >
                    <div
                        className="bg-white max-w-sm w-full p-6 border border-sky-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold">
                            Install Hahnemann
                        </div>
                        <h3 className="font-serif text-2xl text-stone-900 mt-2 mb-4">
                            Add to your iPhone Home Screen
                        </h3>
                        <ol className="space-y-3 text-sm text-stone-700">
                            <li className="flex items-start gap-3">
                                <span className="text-sky-700 font-bold">1.</span>
                                <span className="flex items-center gap-2">
                                    Tap the <Share className="w-4 h-4 inline" /> Share button in Safari's bottom bar
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-sky-700 font-bold">2.</span>
                                <span className="flex items-center gap-2">
                                    Scroll and tap <Plus className="w-4 h-4 inline" /> Add to Home Screen
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-sky-700 font-bold">3.</span>
                                <span>Tap Add — the Hahnemann icon will appear on your home screen.</span>
                            </li>
                        </ol>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={() => setShowIOSCard(false)}
                                className="flex-1 border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50"
                                data-testid="pwa-ios-close"
                            >
                                Close
                            </button>
                            <button
                                onClick={dismiss}
                                className="flex-1 bg-sky-700 hover:bg-sky-800 text-stone-50 px-4 py-2 text-sm"
                                data-testid="pwa-ios-dismiss"
                            >
                                Don't show again
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
